// Designer editor for a handler='wizard' action button — "start a Transaction
// Wizard (obj_type U) from this record".
//
// The wizard picker lists the project's wizards, the ones built on THIS
// transaction first. Once one is chosen its declared `inputs` (design §5.2) are
// the rows of the parameter grid: each takes a template — {form.COLUMN},
// {header.COLUMN} or a literal — resolved in the browser against the clicked
// row exactly like an 'assistant' button's param_map. Inputs grant nothing:
// the wizard's own grant, its steps and the base transaction's rules decide
// what happens. A typed name is still accepted, so a wizard that does not
// exist yet can be wired up first; Check (engine validation) reports a param
// that names no input of the wizard.

import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Wand2 } from 'lucide-react'
import type { ActionDef, WizardActionBinding } from './types'
import type { WizardInput } from '@twasta/page-runtime/wizard/contract'
import { listWizards, loadWizard, type WizardSummary } from '@/api/wizards'
import ObjectPickerField from '../page/ObjectPickerField'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9375rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontWeight: 600 }
const hintStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }
const addBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)',
  fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 3,
}
const sectionHead: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px',
}

/** A wizard's declared launch inputs, loaded once the name settles. `null`
 *  while unknown (no name, still loading, or the load failed) — the grid then
 *  takes typed names. */
export function useWizardInputs(project: string, wizardName: string): WizardInput[] | null {
  const [inputs, setInputs] = useState<WizardInput[] | null>(null)
  useEffect(() => {
    const name = wizardName.trim()
    if (!project || !name) { setInputs(null); return undefined }
    let cancelled = false
    const t = setTimeout(() => {
      loadWizard(project, name)
        .then((w) => {
          if (cancelled) return
          setInputs((w?.inputs || []).filter((x): x is WizardInput => !!x && typeof x.name === 'string' && !!x.name))
        })
        .catch(() => { if (!cancelled) setInputs(null) })
    }, 400)
    return () => { cancelled = true; clearTimeout(t) }
  }, [project, wizardName])
  return inputs
}

export default function WizardActionEditor({
  def, onPatch, project, sourceObjName, formColumns, headerColumns, actionIndex,
}: {
  def: ActionDef
  onPatch: (patch: Partial<ActionDef>) => void
  project: string
  /** The transaction this button sits on — its wizards are offered first. */
  sourceObjName?: string
  /** This form's / its header's columns — offered as {form.X} / {header.X}. */
  formColumns: string[]
  headerColumns: string[]
  /** Distinguishes this card's suggestion lists from its neighbours'. */
  actionIndex: number
}) {
  const b: WizardActionBinding = def.wizard || { obj_name: '' }
  const patch = (p: Partial<WizardActionBinding>) => onPatch({ wizard: { ...b, ...p } })
  const objName = (b.obj_name || '').trim()
  const paramMap: Record<string, unknown> = b.param_map || {}

  const [wizards, setWizards] = useState<WizardSummary[]>([])
  useEffect(() => {
    if (!project) return undefined
    let cancelled = false
    listWizards(project)
      .then(list => { if (!cancelled) setWizards(list) })
      .catch(err => { console.warn('[WizardActionEditor] could not list wizards', err) })
    return () => { cancelled = true }
  }, [project])

  const source = (sourceObjName || '').trim().toLowerCase()
  const catalog = useMemo(() => {
    const own = (w: WizardSummary) => !!source && String(w.base_object || '').toLowerCase() === source
    return [...wizards]
      .sort((x, y) => Number(own(y)) - Number(own(x)) || x.obj_name.localeCompare(y.obj_name))
      .map(w => ({
        obj_name: w.obj_name,
        title: w.title,
        hint: own(w)
          ? `on this transaction${w.mode ? ` · ${w.mode.replace(/_/g, ' ')}` : ''}`
          : (w.base_object ? `on ${w.base_object}` : 'Wizard'),
      }))
  }, [wizards, source])
  const picked = wizards.find(w => w.obj_name.toLowerCase() === objName.toLowerCase())

  const inputs = useWizardInputs(project, objName)
  const declared = useMemo(() => new Set((inputs || []).map(x => x.name)), [inputs])

  const tokenList = `wizard-tokens-${actionIndex}`
  const tokens = useMemo(() => Array.from(new Set([
    ...formColumns.map(c => `{form.${c}}`),
    ...headerColumns.map(c => `{header.${c}}`),
  ])), [formColumns, headerColumns])

  const writeParam = (name: string, value: unknown) => {
    const next: Record<string, unknown> = { ...paramMap }
    if (value === undefined || value === '') delete next[name]
    else next[name] = value
    patch({ param_map: Object.keys(next).length ? next : undefined })
  }
  // Rows not tied to a declared input: an older map, a typed wizard name that
  // has not loaded, or an input the wizard has since dropped.
  const extra = Object.entries(paramMap).filter(([k]) => inputs === null || !declared.has(k))

  // Map every unmapped input to the form column of the same name (or the
  // column its `target` names), the usual case for "edit this record".
  const autoFill = () => {
    const lower = new Map(formColumns.map(c => [c.toLowerCase(), c]))
    const next: Record<string, unknown> = { ...paramMap }
    for (const inp of inputs || []) {
      if (next[inp.name] !== undefined && next[inp.name] !== '') continue
      const tail = String(inp.target || '').split('.').pop() || ''
      const col = lower.get(inp.name.toLowerCase()) || lower.get(tail.toLowerCase())
      if (col) next[inp.name] = `{form.${col}}`
    }
    patch({ param_map: Object.keys(next).length ? next : undefined })
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <datalist id={tokenList}>{tokens.map(t => <option key={t} value={t} />)}</datalist>

      <div style={{ marginBottom: 6 }}>
        <ObjectPickerField
          label="Wizard"
          value={b.obj_name || ''}
          onChange={(name) => patch({ obj_name: name })}
          catalog={catalog}
          typeLabel="wizard"
          placeholder="Pick the wizard to start…"
          emptyHint="No wizards in this project yet — type its name to wire it up first."
        />
        {objName && wizards.length > 0 && !picked && (
          <div style={{ ...hintStyle, color: '#e6a23c', marginTop: 3 }}>
            No wizard named “{objName}” in this project yet.
          </div>
        )}
        {picked && source && picked.base_object
          && String(picked.base_object).toLowerCase() !== source && (
          <div style={{ ...hintStyle, marginTop: 3 }}>
            This wizard works on <strong>{picked.base_object}</strong>, not this transaction.
          </div>
        )}
      </div>

      <div style={sectionHead}>
        <span style={labelStyle}>Launch inputs (wizard input ← screen value)</span>
        <span style={{ display: 'flex', gap: 8 }}>
          {!!inputs?.length && (
            <button style={addBtn} title="Map each empty input to the form column of the same name"
              onClick={autoFill}>
              <Wand2 size={11} /> Match columns
            </button>
          )}
          <button style={addBtn} onClick={() => {
            let n = Object.keys(paramMap).length + 1
            while (paramMap[`input_${n}`] !== undefined) n += 1
            patch({ param_map: { ...paramMap, [`input_${n}`]: '' } })
          }}>
            <Plus size={11} /> Add
          </button>
        </span>
      </div>

      {(inputs || []).map(inp => {
        const raw = paramMap[inp.name]
        return (
          <div key={inp.name} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
            <span title={[inp.label, inp.type, inp.target ? `→ ${inp.target}` : ''].filter(Boolean).join(' · ')}
              style={{ ...labelStyle, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <code>{inp.name}</code>
              {inp.required && <span style={{ color: 'var(--color-error, #cf222e)' }}> *</span>}
              {inp.label && inp.label !== inp.name ? ` — ${inp.label}` : ''}
            </span>
            <input style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              value={raw === undefined || raw === null ? '' : String(raw)}
              placeholder={inp.required ? 'required — {form.COLUMN} or literal' : '{form.COLUMN} or literal'}
              list={tokenList}
              onChange={e => writeParam(inp.name, e.target.value)} />
            <select style={{ ...inputStyle, width: 26, flex: '0 0 auto', padding: '5px 2px' }}
              value="" title="Pick a column"
              onChange={e => { if (e.target.value) writeParam(inp.name, e.target.value) }}>
              <option value="">…</option>
              {tokens.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )
      })}

      {extra.map(([k, v], ei) => (
        <div key={`extra-${ei}`} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
          <input style={{
            ...inputStyle, flex: 1, minWidth: 0,
            ...(inputs !== null ? { border: '1px solid #e6a23c' } : {}),
          }}
            value={k} placeholder="wizard input"
            title={inputs !== null ? 'The wizard declares no input of this name' : undefined}
            list={inputs ? `wizard-inputs-${actionIndex}` : undefined}
            onChange={e => {
              const entries = Object.entries(paramMap)
              const at = entries.findIndex(([x]) => x === k)
              if (at >= 0) entries[at] = [e.target.value, v]
              patch({ param_map: Object.fromEntries(entries) })
            }} />
          <input style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            value={v === undefined || v === null ? '' : String(v)}
            placeholder="{form.COLUMN} or literal" list={tokenList}
            onChange={e => patch({ param_map: { ...paramMap, [k]: e.target.value } })} />
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
            title="Remove"
            onClick={() => {
              const next = { ...paramMap }
              delete next[k]
              patch({ param_map: Object.keys(next).length ? next : undefined })
            }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      {inputs && (
        <datalist id={`wizard-inputs-${actionIndex}`}>
          {inputs.map(x => <option key={x.name} value={x.name}>{x.label || ''}</option>)}
        </datalist>
      )}
      <div style={hintStyle}>
        {inputs && !inputs.length
          ? 'This wizard declares no launch inputs — it starts empty.'
          : <>
              <code>{'{form.COLUMN}'}</code> binds the clicked record&rsquo;s column,{' '}
              <code>{'{header.COLUMN}'}</code> its header form&rsquo;s; anything else is a literal.
              An input left unmapped is asked by the wizard (or takes its default).
            </>}
      </div>

      <label style={labelStyle}>Show it</label>
      <select style={{ ...inputStyle, marginBottom: 6 }} value={b.presentation === 'drawer' ? 'drawer' : 'navigate'}
        onChange={e => patch({ presentation: e.target.value as 'navigate' | 'drawer' })}>
        <option value="navigate">On its own screen (Back returns here)</option>
        <option value="drawer">Over this screen, in a drawer (refreshes it on close)</option>
      </select>
      <label style={labelStyle}>Title (optional)</label>
      <input style={{ ...inputStyle, marginBottom: 6 }} value={b.title || ''} placeholder={def.name || 'Defaults to the button name'}
        onChange={e => patch({ title: e.target.value || undefined })} />
      <div style={hintStyle}>
        Nothing runs on the server when the button is pressed — the wizard opens with these
        inputs, and its own grant, steps and this transaction&rsquo;s rules decide what is saved.
      </div>
    </div>
  )
}
